/* eslint-disable import/no-cycle */
import { createReduxModule } from "hooks-for-redux";
import { StatusCodes } from "http-status-codes";
import getIsProfileValid from "../../api/get/getIsProfileValid";
import {
  FormWizardOption,
  IErrorPage,
  IFlyoutObject,
  IGettingStartedValues,
  ITotalCost,
  IWizardInitialState,
  ProgressBarOption,
} from "./WizardReducer.types";
import { wizardReducerInitialState } from "./WizardReducerInitialState";
import { PaymentAttemptStatusEnum } from "../../utils/enums/paymentAttemptStatusEnum";
import getIsNewMember, {
  IIsNewMemberResponse,
} from "../../api/get/getIsNewMember";
import getBusinessAddress from "../../../common/api/get/getAddresses/getBusinessAddress";
import { getCountryCodeByCountryName } from "../../../common/utils/helpers/phoneHelper";
import { getIsZipCodeRequired } from "../../../common/utils/helpers/countryHelper";
import { setProfileValidationData } from "../ProfileReducer/ProfileReducer";

export const [
  useWizard,
  {
    NavigateToFormPage,
    NavigateToNextPage,
    UpdateAttestProduction,
    UpdateBeforeUpgradeMembershipLevel,
    UpdateCompanyAdminImpersonated,
    UpdateCompletedRenewalMembership,
    UpdateCurrentPage,
    UpdateCurrentStep,
    UpdateErrorPage,
    UpdateFlyoutObject,
    UpdateFormOptions,
    UpdateGettingStartedPageFlag,
    UpdateGettingStartedValues,
    UpdateGreetingPageFlag,
    UpdateIsNewMember,
    UpdateIsProfileValid,
    UpdateMembershipLevel,
    UpdateMembershipType,
    UpdateMembershipTypePageFlag,
    UpdatePages,
    UpdatePaymentMethod,
    UpdatePaymentStatus,
    UpdateProductionPageFlag,
    UpdateProductionQualifiedMembershipLevel,
    UpdateProgressBarOptions,
    UpdateQualifiedMembershipLevel,
    UpdateQualifyingMethod,
    UpdateQueryParamPersonId,
    UpdateRedirectResult,
    UpdateReviewPageFlag,
    UpdateShowPaymentPage,
    UpdateShowProfilePage,
    UpdateShowRoutingPage,
    UpdateSkipMembershipTypeAndProduction,
    UpdateSkipProductionPage,
    UpdateTermsAndConditionsAccepted,
    UpdateTotWaiverUsed,
    UpdateTotalCostToPay,
    UpdateUpgrade,
    UpdateUploadedDocumentNames,
    UpdateUserAgreedToPayBackdues,
    UpdateUserAttestedProduction,
    setWizardState,
  },
  wizardStore,
] = createReduxModule("wizard", wizardReducerInitialState, {
  // Reducers
  setWizardState: (state, data: IWizardInitialState) => ({
    ...state,
    ...data,
  }),
  UpdateCompanyAdminImpersonated: (
    state,
    companyAdminImpersonated: boolean
  ) => ({
    ...state,
    companyAdminImpersonated,
  }),
  UpdateQueryParamPersonId: (state, id: number) => ({
    ...state,
    queryParamPersonId: id,
  }),
  UpdateAttestProduction: (state, value: boolean) => ({
    ...state,
    attestProduction: value,
  }),
  UpdateUserAttestedProduction: (state, value: boolean) => ({
    ...state,
    userAttestedProduction: value,
  }),
  UpdateUserAgreedToPayBackdues: (state, value: boolean) => ({
    ...state,
    userAgreedToPayBackdues: value,
  }),
  UpdateUploadedDocumentNames: (state, uploadedDocumentNames: string[]) => ({
    ...state,
    uploadedDocumentNames,
    totalNumberOfUploadedDocuments: uploadedDocumentNames.length,
  }),
  UpdateCurrentPage: (state, pageIndex: number) => ({
    ...state,
    currentPage: pageIndex,
  }),
  NavigateToNextPage: (state) => ({
    ...state,
    currentPage: state.currentPage + 1,
  }),
  UpdateCurrentStep: (state, currentStep: string) => ({
    ...state,
    currentStep,
  }),
  UpdateGreetingPageFlag: (state, visited: boolean) => ({
    ...state,
    greetingPageVisited: visited,
  }),
  UpdateGettingStartedPageFlag: (state, visited: boolean) => ({
    ...state,
    gettingStartedPageVisited: visited,
  }),
  UpdateMembershipTypePageFlag: (state, visited: boolean) => ({
    ...state,
    membershipTypePageVisited: visited,
  }),
  UpdateProductionPageFlag: (state, visited: boolean) => ({
    ...state,
    productionPageVisited: visited,
  }),
  UpdateReviewPageFlag: (state, visited: boolean) => ({
    ...state,
    reviewPageVisited: visited,
  }),
  UpdatePages: (state, page: string) => {
    const newState: string[] = [...state.pages];

    newState.push(page);

    return {
      ...state,
      pages: newState,
      furthestCompletedPage: newState.length - 1,
    };
  },
  UpdateSkipProductionPage: (state, skip: boolean) => ({
    ...state,
    skipProductionPage: skip,
  }),
  UpdateSkipMembershipTypeAndProduction: (state, skip: boolean) => ({
    ...state,
    skipMembershipTypeAndProduction: skip,
  }),
  UpdateErrorPage: (state, showErrorObj: IErrorPage) => ({
    ...state,
    errorPage: { ...showErrorObj },
  }),
  UpdateMembershipLevel: (state, level: string) => ({
    ...state,
    membershipLevel: {
      ...state.membershipLevel,
      selectedMembershipLevel: level,
    },
  }),
  UpdateQualifiedMembershipLevel: (state, level: string) => ({
    ...state,
    membershipLevel: {
      ...state.membershipLevel,
      qualifiedMembershipLevel: level,
    },
  }),
  UpdateProductionQualifiedMembershipLevel: (state, level: string) => ({
    ...state,
    membershipLevel: {
      ...state.membershipLevel,
      productionQualifiedMembershipLevel: level,
    },
  }),
  UpdateBeforeUpgradeMembershipLevel: (state, level: string) => ({
    ...state,
    membershipLevel: {
      ...state.membershipLevel,
      beforeUpgradeMembershipLevel: level,
    },
  }),
  UpdateGettingStartedValues: (
    state,
    gettingStartedValues: IGettingStartedValues
  ) => ({
    ...state,
    gettingStarted: {
      answers: { ...gettingStartedValues },
    },
  }),
  UpdateMembershipType: (state, membershipType: string) => ({
    ...state,
    membershipType,
  }),
  UpdateFlyoutObject: (state, flyoutObject: IFlyoutObject) => {
    const notificationText = flyoutObject.notificationText
      ? flyoutObject.notificationText
      : state.flyoutObject.notificationText;

    let returnObj: IWizardInitialState;

    if (flyoutObject.level && flyoutObject.level !== " ") {
      returnObj = {
        ...state,
        membershipLevel: {
          ...state.membershipLevel,
          qualifiedMembershipLevel: flyoutObject.level,
          selectedMembershipLevel: flyoutObject.level,
        },
        flyoutObject: {
          ...state.flyoutObject,
          display: flyoutObject.display,
          levelImgSrc: flyoutObject.levelImgSrc,
          notificationText,
        },
      };
    } else {
      returnObj = {
        ...state,
        flyoutObject: {
          ...state.flyoutObject,
          display: flyoutObject.display,
          levelImgSrc: flyoutObject.levelImgSrc,
          notificationText,
        },
      };
    }

    return {
      ...returnObj,
    };
  },
  UpdateFormOptions: (state, options: FormWizardOption[]) => ({
    ...state,
    formOptions: options,
  }),
  UpdateProgressBarOptions: (state, options: ProgressBarOption[]) => ({
    ...state,
    progressOptions: options,
  }),
  NavigateToFormPage: (state, targetIndex: number) => {
    const newFormOptions = [...state.formOptions];

    newFormOptions.forEach((el, index) => {
      if (index < targetIndex) el.visited = true;
      if (index === targetIndex) {
        el.active = true;
        return;
      }
      el.active = false;
    });

    return {
      ...state,
      formOptions: newFormOptions,
      currentPage: targetIndex,
    };
  },
  UpdateShowPaymentPage: (state, showPaymentPage: boolean) => ({
    ...state,
    showPaymentPage,
  }),
  UpdateTotalCostToPay: (state, totalCostToPay: ITotalCost) => ({
    ...state,
    totalCostToPay: { ...totalCostToPay },
  }),
  UpdateShowProfilePage: (state, show: boolean) => ({
    ...state,
    showProfilePage: show,
  }),
  UpdateShowRoutingPage: (state, show: boolean) => ({
    ...state,
    showRoutingPage: show,
  }),
  UpdateRedirectResult: (state, urlSearchValue: string | null) => ({
    ...state,
    redirectResult: urlSearchValue,
  }),
  UpdateIsProfileValid: (state, isProfileValid: boolean) => ({
    ...state,
    isProfileValid,
  }),
  UpdateIsNewMember: (state, data: IIsNewMemberResponse) => ({
    ...state,
    newMemberResponse: { ...data },
  }),
  UpdateCompletedRenewalMembership: (state, completed: boolean) => ({
    ...state,
    completedRenewalMembership: completed,
  }),
  UpdatePaymentStatus: (state, status: PaymentAttemptStatusEnum) => ({
    ...state,
    paymentStatus: status,
  }),
  UpdatePaymentMethod: (state, paymentMethod: string | null) => ({
    ...state,
    paymentMethod,
  }),
  UpdateTermsAndConditionsAccepted: (
    state,
    termsAndConditionsAccepted: boolean
  ) => ({
    ...state,
    termsAndConditionsAccepted,
  }),
  UpdateUpgrade: (state, upgrade: boolean) => ({
    ...state,
    upgrade,
  }),
  UpdateTotWaiverUsed: (state, totWaiverUsed: boolean) => ({
    ...state,
    totWaiverUsed,
  }),
  UpdateQualifyingMethod: (state, qualifyingMethod: number | null) => ({
    ...state,
    qualifyingMethod,
  }),
});

export const updateIsNewMemberFromAPI = async (
  personId: number,
  jwt: string
) => {
  const res = await getIsNewMember(personId, jwt);

  if (res?.status !== StatusCodes.OK) return res?.status;
  UpdateIsNewMember(res.data);

  return StatusCodes.OK;
};

export const updateIsProfileValidFromAPI = async (
  personId: number,
  jwt: string
) => {
  const profileValidRes = await getIsProfileValid(personId, jwt);
  const addressRes = await getBusinessAddress(jwt, personId);

  if (profileValidRes) {
    if (profileValidRes.status !== StatusCodes.OK)
      return profileValidRes.status;

    setProfileValidationData(profileValidRes.data);

    const {
      lastNameIsValid,
      dateOfBirthIsValid,
      emailAddressIsValid,
      phoneIsValid,
      addressIsValid,
      cityIsValid,
      postalCodeIsValid,
      countryIsValid,
    } = profileValidRes.data;

    let zipCodeValid = true;

    if (addressRes && addressRes.status === StatusCodes.OK) {
      const countryCode = getCountryCodeByCountryName(
        addressRes.data.country ?? ""
      );

      const zipCodeRequired = countryCode
        ? getIsZipCodeRequired(countryCode)
        : true;

      if (!postalCodeIsValid) zipCodeValid = !zipCodeRequired;
    }

    const profileValid =
      lastNameIsValid &&
      dateOfBirthIsValid &&
      emailAddressIsValid &&
      phoneIsValid &&
      addressIsValid &&
      cityIsValid &&
      zipCodeValid &&
      countryIsValid;

    UpdateIsProfileValid(profileValid);

    return profileValid;
  }

  return StatusCodes.INTERNAL_SERVER_ERROR;
};
